
import ResponsiveTable from '../../../components/ResponsiveTable';
import { hideLoading, showLoading } from '../../../loading';
import request from '../../../request';
import swal from 'sweetalert';
import PillLabel from '../../../components/PillLabel';
import { connect } from 'react-redux';
import { USER_ROLE } from '../../../../../shared/constants';
import { formatMoney, hasIntersection } from '../../../../../shared/utils';
import { Button, Dialog, DialogContent, Link } from '@mui/material';
import StyledLink from '../../../components/StyledLink';
import links from '../../../links';
import AttachmentList from '../../../components/AttachmentList';
import DialogTitleWithCloseButton from '../../../components/DialogTitleWithCloseButton';
import DateTime from '../../../components/DateTime';
import LabeledContainer from '../../../components/LabeledContainer';
import NavigationStateSaver from '../../../components/NavigationStateSaver';
import Payout from './Payout';

class _AgentEarnings extends NavigationStateSaver {

   state = {
      agent_earnings: null,
      page: 0,
      limit: 10,
      total: 0,
      payoutModalOpen: false,
      selectedEarning: null,
      filters: {
         collected: undefined,
         from: undefined,
         to: undefined,
      },
      showingPayments: false
   }

   showPayments = selectedEarning => {
      this.setState({ selectedEarning, showingPayments: true });
   }

   closePayments = () => {
      this.setState({ selectedEarning: null, showingPayments: false});
   }

   openPayoutModal = (selectedEarning) => {
      this.setState({ payoutModalOpen: true, selectedEarning });
   }

   closePayoutModal = (earning) => {
      this.setState({ payoutModalOpen: false, selectedEarning: null });

      if (earning) {
         const agent_earnings = this.state.agent_earnings.map(e => e._id === earning._id ? earning : e);
         this.setState({ agent_earnings });
      }
   }

   fetchEarnings = async () => {

      try {

         showLoading();

         const params = {
            page: this.state.page + 1,
            limit: this.state.limit
         }

         const { collected, from, to } = this.state.filters;
         if (typeof collected === 'boolean') params.collected = collected;
         if (from) params.from = from;
         if (to) params.to = to;

         const res = await request.get('/api/general/agent-earnings', { params });
         const { agent_earnings, total } = res.data;

         this.setState({ agent_earnings, total });

      } catch (err) {
         swal('Error', err.message, 'error');
      } finally {
         hideLoading();
      }
      
   }

   async componentDidMount() {
      await super.componentDidMount();

      if (!this.state.agent_earnings)
         this.fetchEarnings();
   }
   
   render() {

      const canPayout = hasIntersection(this.props.roles, [ USER_ROLE.SUPER_ADMIN, USER_ROLE.FINANCE_MANAGER ]);

      return <>
         <ResponsiveTable
            cols={[
               {
                  field: 'agent',
                  caption: 'Agent',
                  render: agent => agent ? <StyledLink to={links.agent(agent._id)}>
                     {agent.name} {agent.surname}
                  </StyledLink> : '–'
               },
               {
                  field: 'agent_type',
                  caption: 'Agent Type',
                  render: type => <PillLabel className='uppercase'>{type}</PillLabel>
               },
               {
                  field: 'amount',
                  caption: 'Amount',
                  render: amount => formatMoney(amount)
               },
               {
                  field: 'paid',
                  caption: 'Paid',
                  render: paid => formatMoney(paid)
               },
               {
                  field: 'for',
                  caption: 'For',
                  render: (_, row) => {

                     const { loan, installation } = row;
                     let caption, to;

                     if (loan) {
                        caption = 'Loan';
                        to = links.loan(loan._id);
                     } else if (installation) {
                        caption = 'Installation';
                        to = links.installation(installation._id);
                     } else {
                        return null
                     }

                     return to && <StyledLink to={to}>
                        {caption}
                     </StyledLink>
                  }
               },
               {
                  field: 'payments',
                  caption: 'Payments',
                  render: (payments, earning) => payments?.length ? <Link className='text-blue-500 cursor-pointer hover:underline' onClick={() => this.showPayments(earning)}>
                     {payments.length} payment{payments.length > 1 ? 's' : ''}
                  </Link> : '–'
               },
               {
                  field: 'actions',
                  caption: '',
                  render: (_, row) => {
                     return <>
                        {
                           canPayout && <Button
                              variant='contained'
                              size='small'
                              className='text-xs rounded-full'
                              onClick={() => this.openPayoutModal(row)}
                              disabled={row.collected}
                           >
                              Payout
                           </Button>
                        }
                     </>
                  }
               }
            ]}
            rows={this.state.agent_earnings}
            fullHeight
            pagination={{
               page: this.state.page,
               count: this.state.total,
               rowsPerPage: this.state.limit,
               labelRowsPerPage: 'Earnings per page',
               onPageChange: (_, page) => this.setState({ page }, this.fetchEarnings),
               onRowsPerPageChange: e => this.setState({ limit: e.target.value }, this.fetchEarnings)
            }}
            onRefresh={this.fetchEarnings}
            filters={{
               onChange: filters => this.setState({ filters, page: 0 }, this.fetchEarnings),
               filters: [
                  {
                     field: 'collected',
                     label: 'Collected',
                     type: 'boolean',
                     value: this.state.filters.collected
                  },
                  {
                     field: 'from',
                     label: 'From',
                     type: 'date',
                     value: this.state.filters.from
                  },
                  {
                     field: 'to',
                     label: 'To',
                     type: 'date',
                     value: this.state.filters.to
                  }
               ]
            }}
         />
         {
            this.state.payoutModalOpen && <Payout
               {...this.state.selectedEarning}
               onClose={this.closePayoutModal}
            />
         }
         {
            this.state.showingPayments && <Dialog open>
               <DialogTitleWithCloseButton onClose={this.closePayments}>
                  Payments for {this.state.selectedEarning.agent.name} {this.state.selectedEarning.agent.surname}
               </DialogTitleWithCloseButton>
               <DialogContent dividers>
                  <div className='w-full max-w-[300px]'>
                     {
                        this.state.selectedEarning.payments.map(payment => {
                           const { amount, created_at, attachments, type } = payment;

                           return <div className='[&:not(:last-child)]:border-b py-4 text-gray-600 flex flex-col gap-4'>
                              <div className='grid grid-cols-[1fr,auto]'>
                                 <div className='text-2xl font-bold'>
                                    {formatMoney(amount)}
                                 </div>
                                 <div className='flex items-center'>
                                    <DateTime value={created_at} />
                                 </div>
                              </div>
                              <LabeledContainer label="Attachments" className='rounded-lg p-4'>
                                 <AttachmentList
                                    attachments={attachments}
                                    variant="list"
                                 />
                              </LabeledContainer>
                              <div className='text-right'>
                                 <PillLabel className='uppercase bg-orange-500 text-white'>
                                    {type}
                                 </PillLabel>
                              </div>
                           </div>
                        })
                     }
                  </div>
               </DialogContent>
            </Dialog>
         }
      </>
   }
}

const mapStateToProps = state => ({ roles: state.user?.roles || [] });
const AgentEarnings = connect(mapStateToProps)(_AgentEarnings);

export default AgentEarnings;